import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Download, Globe, Smartphone } from "lucide-react";
import { APP_STORE_URL, IS_APP_STORE_LIVE } from "../config/appLinks";
import {
  FALLBACK_APK_URL,
  OFFICIAL_DOWNLOAD_PAGE_URL,
  UPDATE_API_URL,
  buildQrCodeUrl,
  formatApkSize,
} from "../utils/downloadPageHelpers";

const INSTALL_STEPS = [
  "Tap Download.",
  "Open the file from your notifications or Files.",
  "If Android asks, allow this browser to install apps.",
];

const HELP_ITEMS = [
  {
    question: "Install is blocked",
    answer:
      "Settings → Apps → Chrome or Files → Install unknown apps → Allow. Then open the APK again.",
  },
  {
    question: "Is this the official app?",
    answer:
      "Yes, if you started here at betbond.app/download. The Android package is com.betbond. Do not install files from WhatsApp or Telegram.",
  },
  {
    question: "Why not Google Play?",
    answer:
      "Real-money betting apps are restricted on Google Play in Rwanda. The official Android app is this APK.",
  },
];

function HelpItem({ question, answer }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border-b border-white/10 last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-center justify-between gap-3 py-3.5 text-left"
      >
        <span className="text-sm text-white/80">{question}</span>
        <ChevronDown
          size={16}
          className={`shrink-0 text-white/40 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && <p className="pb-3.5 text-sm leading-relaxed text-white/55">{answer}</p>}
    </div>
  );
}

export default function DownloadApp() {
  const [release, setRelease] = useState(null);

  useEffect(() => {
    let active = true;

    fetch(UPDATE_API_URL)
      .then((response) => {
        if (!response.ok) throw new Error("Release information is unavailable");
        return response.json();
      })
      .then((data) => {
        if (active) setRelease(data);
      })
      .catch(() => {
        if (active) setRelease(null);
      });

    return () => {
      active = false;
    };
  }, []);

  const apkUrl = release?.apkUrl || FALLBACK_APK_URL;
  const versionName = release?.latestVersionName;
  const fileSize = formatApkSize(release?.sizeBytes);
  const meta = [versionName ? `v${versionName}` : null, fileSize].filter(Boolean).join(" · ");
  const qrUrl = buildQrCodeUrl(OFFICIAL_DOWNLOAD_PAGE_URL);

  return (
    <main className="min-h-screen bg-black px-4 pb-24 pt-28 text-white sm:px-6">
      <div className="mx-auto max-w-lg text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#64B5F6]">
          Official Android app
        </p>
        <h1 className="mt-3 text-3xl font-extrabold tracking-tight sm:text-4xl">
          Download BetBond
        </h1>
        <p className="mt-3 text-base text-white/60">
          Tap the button on your phone. Then open the file.
        </p>

        <a
          href={apkUrl}
          className="mt-8 inline-flex w-full items-center justify-center gap-3 rounded-2xl bg-[#1E88E5] px-5 py-4 text-lg font-bold text-white transition hover:bg-[#1976D2] focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
        >
          <Download size={22} />
          Download
        </a>

        <p className="mt-3 text-xs text-white/40">
          {meta ? `${meta} · ` : ""}18+ · Rwanda
        </p>

        <ol className="mt-10 space-y-3 text-left">
          {INSTALL_STEPS.map((step, index) => (
            <li key={step} className="flex items-center gap-3 text-sm text-white/70">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/[0.06] text-xs font-bold text-[#64B5F6]">
                {index + 1}
              </span>
              <span>{step}</span>
            </li>
          ))}
        </ol>

        <div className="mt-12 hidden border-t border-white/10 pt-10 sm:block">
          <p className="text-sm text-white/50">On a computer? Scan this with your phone.</p>
          <img
            src={qrUrl}
            alt="QR code for betbond.app/download"
            className="mx-auto mt-4 rounded-xl bg-white p-2"
            width={168}
            height={168}
          />
        </div>

        <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:justify-center">
          {IS_APP_STORE_LIVE ? (
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/12 px-4 py-3 text-sm font-semibold text-white/80 hover:bg-white/[0.04]"
            >
              <Smartphone size={16} />
              iPhone · App Store
            </a>
          ) : (
            <Link
              to="/app/welcome"
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/12 px-4 py-3 text-sm font-semibold text-white/80 hover:bg-white/[0.04]"
            >
              <Smartphone size={16} />
              iPhone · Open web app
            </Link>
          )}
          <Link
            to="/app/welcome"
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/12 px-4 py-3 text-sm font-semibold text-white/80 hover:bg-white/[0.04]"
          >
            <Globe size={16} />
            Use in browser
          </Link>
        </div>

        <section className="mt-14 text-left">
          <h2 className="text-xs font-semibold uppercase tracking-[0.16em] text-white/35">
            Help
          </h2>
          <div className="mt-2">
            {HELP_ITEMS.map((item) => (
              <HelpItem key={item.question} question={item.question} answer={item.answer} />
            ))}
          </div>
          <a
            href="mailto:betbondofficial@gmail.com"
            className="mt-5 inline-block text-sm text-white/40 hover:text-white/70"
          >
            betbondofficial@gmail.com
          </a>
        </section>
      </div>
    </main>
  );
}
